import { format, formatDistance, parseISO } from 'date-fns';
import moment from 'moment';

import { TimeFrameEnumType, TimeframeOptionsFM } from 'models/TimeframeModels';

import {
  DefaultTimeframeStartDateType,
  MaxTimeframeStartDateType,
  MinTimeframeStartDate,
} from './constants';

export const Today = moment().startOf('day').toDate();
export const StartDateDec2020 = moment('2020-12-01').toDate();
export const EndDate12mo = moment().startOf('day').add(1, 'year').toDate();

export const formatDate = (d: string, dateFormat = 'MMM d, yyyy') => {
  if (!d) return null;
  return format(parseISO(d), dateFormat);
};
export const formatDateISO = (d: string, dateFormat = 'MMM d, yyyy') => {
  if (!d) return null;
  return format(parseIsoDateStartOfDayUtc0(d), dateFormat);
};
export const formatISO = (d: Date, dateFormat = 'MMM d, yyyy') => {
  if (!d) return null;
  const dateIso = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return format(dateIso, dateFormat);
};

/**
 * @deprecated This function should be using formatDateISO function.
 *             Without the parseIsoDateStartOfDayUtc0 there is potential for the start date not to
 *             match what the user has selected
 *             ******USE formatDateISOUntilToday INSTEAD**********
 */
export const formatDateUntilTodayStr = (d: string) => formatDateUntilToday(parseISO(d));

/**
 * @deprecated This function should be using formatDateISO function.
 *             Without the parseIsoDateStartOfDayUtc0 there is potential for the start date not to
 *             match what the user has selected
 *             ******USE formatDateISOUntilToday INSTEAD**********
 */
export const formatDateUntilToday = (d: Date) => {
  if (!d) return null;
  if (isNaN(d.getTime())) return null;
  return `${format(d, 'MMM d, yyyy')} – Today`;
};

/**
 * Formats a date with an emdash Today at the end.
 *
 * Note: YYYY-MM-DD is the format if timezone is wanted to be ignored
 * @param d the date as a string
 * @param dateFormat The format of the formatted date DEFAULT: MMM d, yyyy
 * @returns formatted date with an emdash Today "date – Today"
 */
export const formatDateISOUntilToday = (d: string, dateFormat = 'MMM d, yyyy') => {
  if (!d?.length) return null;
  return `${formatDateISO(d, dateFormat)} – Today`;
};

/**
 * @param {datetime string} d - datetime value is assumed to be UTC
 * @returns today, yesterday, X days ago, X months ago, etc.
 */
export const formatDuration = (d: string) => {
  if (!d) return null;

  // input: ignore time
  const inputDate = parseISO(d.substr(0, d.indexOf('T'))); // remove time

  // today: ignore time
  const dateNow = new Date(Date.now());
  const dateToday = new Date(dateNow.getUTCFullYear(), dateNow.getUTCMonth(), dateNow.getUTCDate());

  const duration = formatDistance(inputDate, dateToday, { addSuffix: true });

  // https://date-fns.org/v2.9.0/docs/formatDistance
  // approximate minutes and hours to today
  if (duration.includes('minute') || duration.includes('hour')) {
    return 'today';
  }

  return duration;
};

type RelativeDateFormatType = 'DurationAgo' | 'AbsoluteDate' | 'AbsoluteAbbrevDate';
export const formatRelativeDate = (d: string, formatType: RelativeDateFormatType) => {
  return formatType === 'DurationAgo'
    ? formatDuration(d)
    : formatType === 'AbsoluteAbbrevDate'
    ? formatDate(d, 'MMM d, yyyy')
    : formatDate(d, 'MMMM d, yyyy');
};

// NOTE: this is called "short" because it is assumed the range is within the same year
export function formatDateOrRangeShort(date1: Date, date2: Date) {
  const m1 = moment(date1).utcOffset(0, false);
  const m2 = date2 && moment(date2).utcOffset(0, false);
  const nDaysDiff = m2 && m2.isValid() ? m2.diff(m1, 'days') : 0;
  return nDaysDiff === 0
    ? m1.format('MMM D, YYYY')
    : m1.get('month') === m2.get('month')
    ? `${m1.format('MMM D')} – ${m2.format('D, YYYY')}`
    : `${m1.format('MMM D')} – ${m2.format('MMM D, YYYY')}`;
}

// NOTE: returns null if null or undefined is passed
export function parseIsoDateStartOfDayUtc0(dateStr: string): Date {
  if (!dateStr) return null;
  return (d => new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))(new Date(dateStr));
}

export function parseDateAsLocal(dateStr: string): Date {
  // NOTE: unlike new Date(), moment() parses the date string in user's timezone with designator not present "2020-03-15"
  return dateStr ? moment(dateStr).toDate() : null;
}

export type LowerBoundDateRangeLiteral = typeof LowerBoundDateRangeTypes[number]; // NOTE: ec070597
export const LowerBoundDateRangeTypes = [
  'last-30-days',
  'last-60-days',
  'last-90-days',
  'last-week',
  'last-year',
  'start-of-today',
] as const;

export function getCurrentUtcDate() {
  return new Date(
    moment().utc().startOf('day').toDate().toLocaleString('en-US', { timeZone: 'UTC' })
  );
}

/**
 * Parses the date string ensuring that it's at the start of the day
 *
 * @param dateString the date to get the start of
 * @returns the start of the supplied date string
 */
export function getStartOfDate(dateString: string) {
  if (!dateString?.length || !moment(dateString).isValid()) return null;
  return moment(dateString).startOf('day').toDate();
}

export function getDateStrToday() {
  return moment().format('YYYY-MM-DD');
}

export function getDatePartStr(dateobj: string | Date) {
  return moment(
    parseIsoDateStartOfDayUtc0(typeof dateobj === 'string' ? dateobj : dateobj.toISOString())
  ).format('YYYY-MM-DD');
}

export function getMomentAgo(dateType: LowerBoundDateRangeLiteral): moment.Moment {
  return dateType === 'last-30-days'
    ? moment().startOf('day').add(-29, 'days')
    : dateType === 'last-60-days'
    ? moment().startOf('day').add(-59, 'days')
    : dateType === 'last-90-days'
    ? moment().startOf('day').add(-89, 'days')
    : dateType === 'last-week'
    ? moment().startOf('day').add(-7, 'days')
    : dateType === 'last-year'
    ? moment().startOf('day').add(-1, 'year')
    : moment().startOf('day');
}

export function isReportDateUrlDeeplinked(reportDate: string, historyDeeplinkDate: string) {
  if (!reportDate || !historyDeeplinkDate) return false;
  return historyDeeplinkDate === format(parseIsoDateStartOfDayUtc0(reportDate), 'yyyy-MM-dd');
}

export function reverseEngineerTimeframeOptionsFM(dateStr: string): TimeframeOptionsFM {
  if (!dateStr) return { timeframeType: '60days', customDate: null };
  const momentObj = moment(dateStr);
  return momentObj.diff(getMomentAgo('last-30-days'), 'days') === 0
    ? { timeframeType: '30days', customDate: null }
    : momentObj.diff(getMomentAgo('last-60-days'), 'days') === 0
    ? { timeframeType: '60days', customDate: null }
    : momentObj.diff(getMomentAgo('last-year'), 'days') === 0
    ? { timeframeType: '1year', customDate: null }
    : { timeframeType: 'custom', customDate: momentObj.toDate() };
}

export function getMomentAgoFromTimeFrameEnumType(timeFrame: TimeFrameEnumType): moment.Moment {
  switch (timeFrame) {
    case '30days':
      return getMomentAgo('last-30-days');
    case '60days':
      return getMomentAgo('last-60-days');
    case '1year':
      return getMomentAgo('last-year');
    default:
      return getMomentAgo('last-60-days');
  }
}

/**
 * Given the argument return a date string and its validity.
 * If as a date the argument violates timeframe range, return DefaultTimeframeStartDateType.
 * If the argument is one of LowerBoundDateRangeLiteral, return that "moment ago" (PT-1908).
 * @param dateStr, either a moment-parseable date string or one of LowerBoundDateRangeLiteral.
 * @returns a tuple: [startDate, isUrlParamValid], date string in YYYY-MM-DD format, if invalid, return DefaultTimeframeStartDateType.
 * Second member is validity flag of the argument as a date.
 */
export function parseDateStr(dateStr: string): [string, boolean] {
  if (!dateStr) return [getMomentAgo(DefaultTimeframeStartDateType).format('YYYY-MM-DD'), true];
  if (LowerBoundDateRangeTypes.includes(dateStr as any))
    return [getMomentAgo(dateStr as any).format('YYYY-MM-DD'), true];
  const dateM = moment(dateStr);
  const isDateStrValid =
    dateM.isValid() &&
    dateM >= moment(MinTimeframeStartDate) &&
    dateM <= getMomentAgo(MaxTimeframeStartDateType);
  return [
    (isDateStrValid ? dateM : getMomentAgo(DefaultTimeframeStartDateType)).format('YYYY-MM-DD'),
    isDateStrValid,
  ];
}

export function getNumOfDaysDiff(startDate: Date, endDate: Date, includeStartDay: boolean = false) {
  if (!startDate || !endDate) return null;

  return (
    moment(endDate).startOf('day').diff(moment(startDate).startOf('day'), 'days') +
    (includeStartDay ? 1 : 0)
  );
}

export function isDateWithin2DaysOfToday(date: Date) {
  if (!date) return false;
  return moment.duration(moment().startOf('day').diff(date)).asDays() <= 2;
}

export function wordTargetVaccinationDateWrtToday(
  targetDate: Date,
  today = new Date(),
  maxMonths: number
) {
  if (!targetDate) return null;
  const mTarget = moment(targetDate);
  const mToday = moment(today);
  return mTarget.diff(mToday, 'minute') < 0
    ? `Completed ${mTarget.format('MMM, YYYY')}`
    : mTarget.diff(mToday, 'month', true) > maxMonths
    ? `>${maxMonths} months`
    : mTarget.date() <= 10
    ? `Early ${mTarget.format('MMMM')}`
    : mTarget.date() <= 20
    ? `Mid ${mTarget.format('MMMM')}`
    : `Late ${mTarget.format('MMMM')}`;
}

export type TargetVaccinationDateStatus =
  | 'complete'
  | 'within-12-months'
  | 'more-than-12-months'
  | 'unavailable';

export function getStatusOfTargetVaccinationDateWrtToday(
  targetDate: Date,
  today = new Date()
): TargetVaccinationDateStatus {
  if (!targetDate) return 'unavailable';
  const mTarget = moment(targetDate);
  const mToday = moment(today);
  return mTarget.diff(mToday, 'minute') < 0
    ? 'complete'
    : mTarget.diff(mToday, 'month', true) <= 12
    ? 'within-12-months'
    : 'more-than-12-months';
}

/**
 * Compares 2 dates and calculates the difference in the passed in comparative values
 *
 * @param firstDate the expected lower date
 * @param secondDate the expected higher date
 * @param compareValue the compared diff value EG (days, months, years)
 * @returns the number of compareValues difference between the 2 dates
 */
export function compareDateDifferences(
  firstDate: Date,
  secondDate: Date,
  compareValue: moment.unitOfTime.Diff
) {
  if (!firstDate || !secondDate || !compareValue) return null;

  return moment(secondDate).diff(firstDate, compareValue);
}
